import { useCMSPageField } from '@/cms/fields'
import { useFavouriteProduct } from '@/domain/product/hooks'

import { useUser } from '@/domain/user/hooks'

import { tok, tokPx } from '@sunwebgroup/ui-components'
import { focusOutline, Icon, Sheet } from '@sunwebgroup/ui-components/uds'

import * as React from 'react'
import { styled } from 'styled-components'
import { LogInSheet } from './LogInSheet'

interface FavoriteButtonProps {
  productId: number
  className?: string
}

const Wrapper = styled.button`
  background-color: ${tok('semantics_buttons_secondary_background_color')};
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.12);
  width: ${tokPx('semantics_controls_m_min_height')};
  height: ${tokPx('semantics_controls_m_min_height')};
  border: none;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${tokPx('semantics_buttons_m_corner_radius')};
  transition:
    color 0.2s ease-in-out,
    background-color 0.2s ease-in-out;

  ${focusOutline()}
  &:hover {
    background-color: ${tok('semantics_buttons_secondary_hover_background_color')};
  }

`

const FilledHeart = styled(Icon)`
  color: ${tok('primitives_color_red_600')};
`

const EmptyHeart = styled(Icon)`
  color: ${tok('semantics_buttons_secondary_content_color')};
  &:hover {
    color: ${tok('primitives_color_red_650')};
  }
`

function FavoriteButton({ productId, className }: FavoriteButtonProps): React.ReactElement {
  const [open, setOpen] = React.useState(false)
  const { isLoggedIn } = useUser()
  const contextItemId = useCMSPageField('contextItemId')
  const { isFavourite, toggleFavouriteState } = useFavouriteProduct(productId, contextItemId)

  const handleClick = React.useCallback(() => {
    if (!isLoggedIn) {
      setOpen(true)
      return
    }

    toggleFavouriteState()
  }, [toggleFavouriteState, isLoggedIn])

  return (
    <Sheet.Root open={open} onOpenChange={(open: boolean) => !open && setOpen(false)}>
      <Sheet.Trigger>
        <Wrapper onClick={handleClick} className={className}>
          {isFavourite ? <FilledHeart glyph="HeartFilled" size={24} /> : <EmptyHeart glyph="Heart" size={24} />}
        </Wrapper>
      </Sheet.Trigger>
      <LogInSheet productId={productId} onClose={() => setOpen(false)} />
    </Sheet.Root>
  )
}

export default styled(FavoriteButton)``
